import { useState, useEffect, useCallback } from 'react'
import * as api from '../services/api'
import AddressCard from './AddressCard'

function AddressList({ onViewEmails, onManageKeys, onManageMFA }) {
  const [addresses, setAddresses] = useState([])
  const [apiKeyCounts, setApiKeyCounts] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadAddresses = useCallback(async () => {
    try {
      setError(null)
      const response = await api.listAddresses()
      const addressList = response.addresses || []
      setAddresses(addressList)

      // Load API key counts for each address
      const counts = {}
      await Promise.all(
        addressList.map(async (address) => {
          try {
            const keysResponse = await api.listApiKeys(address.email)
            counts[address.email] = (keysResponse.api_keys || []).length
          } catch {
            counts[address.email] = 0
          }
        })
      )
      setApiKeyCounts(counts)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAddresses()
  }, [loadAddresses])

  const handleCreateAddress = async () => {
    setIsCreating(true)
    setError(null)

    try {
      await api.createAddress()
      await loadAddresses()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteAddress = async () => {
    if (!deleteConfirm) return

    setIsDeleting(true)
    setError(null)

    try {
      await api.deleteAddress(deleteConfirm.email_address)
      setDeleteConfirm(null)
      await loadAddresses()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Test Email Addresses</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={onManageMFA}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            MFA Devices
          </button>
          <button
            onClick={handleCreateAddress}
            disabled={isCreating}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCreating ? 'Creating...' : 'Create Address'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {addresses.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No addresses</h3>
          <p className="mt-1 text-sm text-gray-500">Create a test email address to get started.</p>
          <button
            onClick={handleCreateAddress}
            disabled={isCreating}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCreating ? 'Creating...' : 'Create Address'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {addresses.map((address) => (
            <AddressCard
              key={address.email}
              address={address}
              apiKeyCount={apiKeyCounts[address.email] || 0}
              onDelete={setDeleteConfirm}
              onViewEmails={onViewEmails}
              onManageKeys={onManageKeys}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Delete Address</h3>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to delete <span className="font-mono font-medium">{deleteConfirm.email_address}</span>?
              This will also delete all emails and API keys for this address.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAddress}
                disabled={isDeleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddressList
